import { EdgeDocumentsApi } from '@trialcard/apigateway.client';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import ProgramModule from './program';
import { Settings } from '~/services/settings';
import { trackEvent, trackException } from '~/services/telemetry';
interface IUploadValidation {
    isInvalidType: boolean | null;
    isInvalidSize: boolean | null;
}

@InjectModule({ stateFactory: true }, module)
export default class DocumentUploadModule extends InjectVuexModule {
    @Resolve
    public settings!: Settings;

    @Resolve
    documentApi!: EdgeDocumentsApi;

    @Resolve
    public program!: ProgramModule;

    public files: VUFile[] = [];
    public validFileTypes = ['application/pdf', 'image/jpg', 'image/jpeg', 'image/png'];
    public maxFileSize = 10000000;
    public pendingUploadSize = 0;
    public totalUploadSize = 0;
    // validation state of incoming upload
    public uploadValidation: IUploadValidation = {
        isInvalidSize: null,
        isInvalidType: null,
    };
    // validation error messages
    public isInvalidSizeError = false;
    public isInvalidTypeError = false;

    @Mutation
    public validateUpload(data: VUFile) {
        const isInvalidSize = data.file.size + this.pendingUploadSize + this.totalUploadSize > this.maxFileSize;
        const isInvalidType = !this.validFileTypes.includes(data.file.type);
        if (!isInvalidSize) {
            this.pendingUploadSize += data.file.size;
        }
        this.uploadValidation = {
            isInvalidSize,
            isInvalidType,
        };
    }

    @Mutation
    setIsInvalidSizeError(value: boolean) {
        this.isInvalidSizeError = value;
    }

    @Mutation
    setIsInvalidTypeError(value: boolean) {
        this.isInvalidTypeError = value;
    }

    @Mutation
    public addFile(data: VUFile) {
        this.files.push(data);
        this.totalUploadSize += data.file.size;
    }

    @Mutation
    public removeFile(data: VUFile) {
        const index = this.files.findIndex(file => file.id === data.id);
        this.files.splice(index, 1);
        this.totalUploadSize -= data.file.size;
        this.isInvalidSizeError = this.totalUploadSize > this.maxFileSize;
    }

    @Mutation
    public clearPendingUploads() {
        this.pendingUploadSize = 0;
    }

    @Mutation
    public resetUploadValidation() {
        this.uploadValidation = {
            isInvalidSize: null,
            isInvalidType: null,
        };
    }

    @Mutation
    public clearStore() {
        this.files = [];
        this.uploadValidation = {
            isInvalidSize: null,
            isInvalidType: null,
        };
        this.isInvalidSizeError = false;
        this.isInvalidTypeError = false;
        this.pendingUploadSize = 0;
        this.totalUploadSize = 0;
    }

    @Action({})
    async uploadDocument(request: { file: File; documentTypeId: string | null }): Promise<{
        success: boolean;
        messages: string[];
        data: any[];
    }> {
        const uploadDocumentResponse = {
            success: false,
            messages: [] as string[],
            data: [],
        };
        const startedAt = Date.now();
        // Tracks whether the HTTP request was actually issued, so the catch
        // block can distinguish "call was never sent" (auth/setup threw) from
        // "call was sent but failed" (server/network error).
        let requestIssued = false;
        const sizeBytes = request.file ? request.file.size : 0;
        const programId = this.program.rebateProgramId;
        try {
            const form = new FormData()
            form.append('programId', programId);
            form.append('memberNumber', this.program.copaySavingsCardId);
            form.append('body[docType]', '1');
            form.append('body[files]', request.file);

            const axios: AxiosInstance = (this.documentApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: '/edge/coupon/Documents/submitDocuments',
                method: 'POST',
                data: form,
                headers: {
                    'x-program-id': programId,
                },
            };
            await (this.documentApi as any).authentications.Token.applyToRequest(axiosRequest);

            trackEvent('DocumentUpload.RequestSending', { programId, target: 'submitDocuments', sizeBytes });
            requestIssued = true;

            // Retry transient upload failures (network blips, brief 5xx). A
            // well-formed "unsuccessful" response is returned immediately
            // below without retrying — only a thrown/network-level failure
            // retries.
            const maxAttempts = 3;
            let lastError: unknown;
            for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                try {
                    const response: AxiosResponse = (await axios.request(axiosRequest)) as AxiosResponse<any>;
                    uploadDocumentResponse.success = !!(response.data && response.data.success && response.data.data);
                    trackEvent('DocumentUpload.RequestCompleted', {
                        programId,
                        target: 'submitDocuments',
                        sizeBytes,
                        success: uploadDocumentResponse.success,
                        attempt,
                        durationMs: Date.now() - startedAt,
                    });
                    return uploadDocumentResponse;
                } catch (error) {
                    lastError = error;
                    if (attempt < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 400 * attempt));
                        continue;
                    }
                }
            }
            throw lastError;
        } catch (error) {
            const err = error as any;
            const status = err && err.response ? err.response.status : undefined;
            const eventName = requestIssued ? 'DocumentUpload.RequestFailed' : 'DocumentUpload.RequestNeverSent';
            trackException(error, {
                event: eventName,
                programId,
                target: 'submitDocuments',
                sizeBytes,
                httpStatus: status,
                durationMs: Date.now() - startedAt,
            });
            trackEvent(eventName, {
                programId,
                target: 'submitDocuments',
                sizeBytes,
                httpStatus: status,
                durationMs: Date.now() - startedAt,
            });
            console.error('error from uploading document:', error);
            return uploadDocumentResponse;
        }
    }

    @Action({})
    async clsuploadDocument(request: { file: File; documentTypeId: string | null }) {
        try {
            const form = new FormData();
            if (request.documentTypeId) form.append('type', request.documentTypeId);
            form.append('attachment', request.file);
            form.append('externalPartyCode', 'CLS');
            form.append('externalProgramCode', 'GSKO');
            const axios: AxiosInstance = (this.documentApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: 'edge/documents/v1/Document/upload',
                method: 'POST',
                data: form,
                headers: {
                    'x-program-id': this.settings.clsProgramId!,
                },
            };
            await Promise.all([
                (this.documentApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
                (this.documentApi as any).authentications.Bearer.applyToRequest(axiosRequest),
                (this.documentApi as any).authentications.Token.applyToRequest(axiosRequest),
            ]);
            const { data } = await axios.request(axiosRequest);
            return data.data && data.success;
        } catch {
            return false;
        }
    }

    @Action({})
    async qpUploadDocument(request: { file: File }): Promise<boolean> {
        const startedAt = Date.now();
        let requestIssued = false;
        const sizeBytes = request.file ? request.file.size : 0;
        const programId = this.program.dynamicQPProgramId;
        try {
            const form = new FormData();
            form.append('attachment', request.file);
            const axios: AxiosInstance = (this.documentApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: 'edge/documents/v1/Document/upload',
                method: 'POST',
                data: form,
                headers: {
                    'x-program-id': programId!,
                },
            };
            await (this.documentApi as any).authentications.Token.applyToRequest(axiosRequest);

            trackEvent('DocumentUpload.RequestSending', { programId, target: 'qpDocumentUpload', sizeBytes });
            requestIssued = true;

            const maxAttempts = 3;
            let lastError: unknown;
            for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                try {
                    const { data } = await axios.request(axiosRequest);
                    const success = !!(data && data.data && data.success);
                    trackEvent('DocumentUpload.RequestCompleted', {
                        programId,
                        target: 'qpDocumentUpload',
                        sizeBytes,
                        success,
                        attempt,
                        durationMs: Date.now() - startedAt,
                    });
                    return success;
                } catch (error) {
                    lastError = error;
                    if (attempt < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 400 * attempt));
                        continue;
                    }
                }
            }
            throw lastError;
        } catch (error) {
            const err = error as any;
            const status = err && err.response ? err.response.status : undefined;
            const eventName = requestIssued ? 'DocumentUpload.RequestFailed' : 'DocumentUpload.RequestNeverSent';
            trackException(error, {
                event: eventName,
                programId,
                target: 'qpDocumentUpload',
                sizeBytes,
                httpStatus: status,
                durationMs: Date.now() - startedAt,
            });
            trackEvent(eventName, {
                programId,
                target: 'qpDocumentUpload',
                sizeBytes,
                httpStatus: status,
                durationMs: Date.now() - startedAt,
            });
            return false;
        }
    }
}
